import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatIDR, formatDateID } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, Search, Pencil, Plus, Trash2, RefreshCw } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyProfile = () => ({
  name: "",
  address: "",
  phone: "",
  email: "",
  npwp: "",
  city: "",
  note: "",
});

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  // Default sort: Nama Pelanggan A→Z (natural). Persisted in localStorage.
  const CUSTOMER_SORT_KEY = "customers_sort_by_v1";
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem(CUSTOMER_SORT_KEY) || "name-asc"; }
    catch { return "name-asc"; }
  });
  useEffect(() => {
    try { localStorage.setItem(CUSTOMER_SORT_KEY, sortBy); } catch { /* ignore */ }
  }, [sortBy]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {originalName, ...profile}
  const [creating, setCreating] = useState(null); // profile obj when creating new
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const syncFromInvoices = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/customers/sync-from-invoices");
      const { scanned = 0, created = 0, updated = 0 } = r.data || {};
      if (created > 0 || updated > 0) {
        toast.success(
          `${created} pelanggan baru dibuat, ${updated} alamat dilengkapi (dari ${scanned} nama unik)`,
        );
      } else {
        toast.info(`Semua ${scanned} pelanggan sudah punya profil.`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal sinkronisasi");
    } finally {
      setSyncing(false);
    }
  };

  const load = async () => {
    try {
      const r = await api.get("/customers");
      setCustomers(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => setCreating(emptyProfile());

  const openEdit = (c) => {
    setEditing({
      originalName: c.name,
      name: c.name,
      address: c.address || "",
      phone: c.phone || "",
      email: c.email || "",
      npwp: c.npwp || "",
      city: c.city || "",
      note: c.note || "",
    });
  };

  const saveNew = async (e) => {
    e.preventDefault();
    if (!creating.name.trim()) {
      toast.error("Nama pelanggan wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.post("/customers", {
        name: creating.name.trim(),
        address: creating.address.trim(),
        phone: creating.phone.trim(),
        email: creating.email.trim(),
        npwp: creating.npwp.trim(),
        city: creating.city.trim(),
        note: creating.note.trim(),
      });
      toast.success("Pelanggan ditambahkan");
      setCreating(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambahkan pelanggan");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) {
      toast.error("Nama pelanggan wajib diisi");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(
        `/customers/${encodeURIComponent(editing.originalName)}`,
        {
          name: editing.name.trim(),
          address: editing.address.trim(),
          phone: editing.phone.trim(),
          email: editing.email.trim(),
          npwp: editing.npwp.trim(),
          city: editing.city.trim(),
          note: editing.note.trim(),
        },
      );
      const updated = r.data?.updated ?? 0;
      toast.success(
        updated > 0
          ? `Profil disimpan · ${updated} faktur diperbarui`
          : "Profil pelanggan disimpan",
      );
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui pelanggan");
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async (name) => {
    if (!window.confirm(`Hapus profil pelanggan "${name}"?\n(Faktur tetap tersimpan)`)) return;
    try {
      await api.delete(`/customers/${encodeURIComponent(name)}`);
      toast.success("Profil pelanggan dihapus");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = !s
      ? customers
      : customers.filter((c) =>
          [c.name, c.address, c.phone, c.email, c.npwp, c.city, c.note]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(s)),
        );
    const sorted = [...base];
    const nameCmp = (a, b) => (a.name || "").localeCompare(b.name || "", "id", { numeric: true });
    switch (sortBy) {
      case "name-desc":
        sorted.sort((a, b) => nameCmp(b, a));
        break;
      case "city-asc":
        sorted.sort((a, b) =>
          (a.city || "").localeCompare(b.city || "", "id") || nameCmp(a, b),
        );
        break;
      case "invoices-desc":
        sorted.sort((a, b) => (Number(b.invoice_count) || 0) - (Number(a.invoice_count) || 0) || nameCmp(a, b));
        break;
      case "total-desc":
        sorted.sort((a, b) => (Number(b.total_amount) || 0) - (Number(a.total_amount) || 0) || nameCmp(a, b));
        break;
      case "total-asc":
        sorted.sort((a, b) => (Number(a.total_amount) || 0) - (Number(b.total_amount) || 0) || nameCmp(a, b));
        break;
      case "name-asc":
      default:
        sorted.sort(nameCmp);
        break;
    }
    return sorted;
  }, [customers, q, sortBy]);

  const totalCustomers = customers.length;
  const totalRevenue = customers.reduce((s, c) => s + Number(c.total_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-zinc-900 text-white">
            <Users className="h-4 w-4" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Data Pelanggan</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Kelola profil pelanggan lengkap dengan No HP, Email, PIC & catatan.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="btn-sync-customers"
            onClick={syncFromInvoices}
            disabled={syncing}
            variant="outline"
            className="rounded-md"
            title="Import semua pelanggan dari faktur yang sudah ada"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sinkron…" : "Sinkron dari Faktur"}
          </Button>
          <Button
            data-testid="btn-add-customer"
            onClick={openCreate}
            size="lg"
            className="bg-zinc-900 hover:bg-zinc-800 shadow-sm"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Data Pelanggan
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="p-4 border-zinc-200 shadow-none rounded-md">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Jumlah Pelanggan</div>
          <div className="mt-1 text-xl font-display font-bold num" data-testid="customer-total-count">
            {totalCustomers}
          </div>
        </Card>
        <Card className="p-4 border-zinc-200 shadow-none rounded-md">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Total Penjualan Semua Pelanggan</div>
          <div className="mt-1 text-xl font-display font-bold num text-emerald-700" data-testid="customer-total-amount">
            {formatIDR(totalRevenue)}
          </div>
        </Card>
      </div>

      <Card className="border-zinc-200 shadow-none rounded-md">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="customer-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama, alamat, HP, email, PIC…"
              className="pl-9 h-10 rounded-md"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-10 w-[210px]" data-testid="customers-sort-by">
              <SelectValue placeholder="Urutkan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name-asc">Nama (A → Z)</SelectItem>
              <SelectItem value="name-desc">Nama (Z → A)</SelectItem>
              <SelectItem value="city-asc">Kota (A → Z)</SelectItem>
              <SelectItem value="invoices-desc">Faktur Terbanyak</SelectItem>
              <SelectItem value="total-desc">Total Belanja Tertinggi</SelectItem>
              <SelectItem value="total-asc">Total Belanja Terendah</SelectItem>
            </SelectContent>
          </Select>
          {(q || sortBy !== "name-asc") && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setQ(""); setSortBy("name-asc"); }}
              className="h-9 text-xs"
              data-testid="customers-filter-reset"
            >
              Reset
            </Button>
          )}
          <div className="text-xs text-zinc-500">
            {filtered.length} dari {totalCustomers} pelanggan
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-zinc-50">
              <TableRow>
                <TableHead className="min-w-[180px]">Nama Pelanggan</TableHead>
                <TableHead className="hidden md:table-cell min-w-[220px]">Alamat</TableHead>
                <TableHead className="hidden lg:table-cell w-[140px]">No HP</TableHead>
                <TableHead className="hidden xl:table-cell w-[180px]">Email</TableHead>
                <TableHead className="hidden xl:table-cell w-[160px]">PIC</TableHead>
                <TableHead className="hidden lg:table-cell w-[120px]">Kota</TableHead>
                <TableHead className="text-right w-[90px]">Faktur</TableHead>
                <TableHead className="text-right w-[150px]">Total Belanja</TableHead>
                <TableHead className="text-right w-[110px]">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="customer-table-body">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-zinc-500">
                    Memuat…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-10 text-zinc-500">
                    Belum ada pelanggan. Klik "Tambah Pelanggan" atau buat faktur penjualan.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow
                    key={c.name}
                    className="hover:bg-zinc-50 transition-colors"
                    data-testid={`customer-row-${c.name.replace(/\s+/g, "_")}`}
                  >
                    <TableCell className="font-medium">
                      <div>{c.name}</div>
                      {c.note && (
                        <div className="text-[11px] text-zinc-500 italic mt-0.5 max-w-[220px] truncate" title={c.note}>
                          {c.note}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-zinc-600 max-w-[280px]">
                      {c.address || <span className="text-zinc-400 italic">—</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-zinc-700 font-mono text-xs">
                      {c.phone || <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-zinc-700 text-xs">
                      {c.email || <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-zinc-700 font-mono text-xs">
                      {c.npwp || <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-zinc-700 text-xs">
                      {c.city || <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right num">{c.invoice_count || 0}</TableCell>
                    <TableCell className="text-right num font-semibold text-emerald-700">
                      {formatIDR(c.total_amount || 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          data-testid={`btn-edit-customer-${c.name.replace(/\s+/g, "_")}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => openEdit(c)}
                          className="h-8 w-8"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {(c.invoice_count || 0) === 0 && (
                          <Button
                            data-testid={`btn-delete-customer-${c.name.replace(/\s+/g, "_")}`}
                            size="icon"
                            variant="ghost"
                            onClick={() => deleteProfile(c.name)}
                            className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            title="Hapus profil"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent data-testid="create-customer-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Tambah Pelanggan Baru</DialogTitle>
            <DialogDescription>
              Data pelanggan akan otomatis muncul saat membuat faktur.
            </DialogDescription>
          </DialogHeader>
          {creating && (
            <form onSubmit={saveNew} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nama Pelanggan <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="create-customer-name"
                  required
                  autoFocus
                  value={creating.name}
                  onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Alamat</Label>
                <Input
                  data-testid="create-customer-address"
                  placeholder="Alamat lengkap"
                  value={creating.address}
                  onChange={(e) => setCreating({ ...creating, address: e.target.value })}
                />
              </div>
              <div>
                <Label>No HP / Telepon</Label>
                <Input
                  data-testid="create-customer-phone"
                  placeholder="0812xxxxxxxx"
                  value={creating.phone}
                  onChange={(e) => setCreating({ ...creating, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Kota</Label>
                <Input
                  data-testid="create-customer-city"
                  placeholder="Contoh: Jakarta Barat"
                  value={creating.city}
                  onChange={(e) => setCreating({ ...creating, city: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input
                  data-testid="create-customer-email"
                  type="email"
                  placeholder="pelanggan@email.com"
                  value={creating.email}
                  onChange={(e) => setCreating({ ...creating, email: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>PIC</Label>
                <Input
                  data-testid="create-customer-npwp"
                  placeholder="XX.XXX.XXX.X-XXX.XXX"
                  value={creating.npwp}
                  onChange={(e) => setCreating({ ...creating, npwp: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Catatan</Label>
                <Input
                  data-testid="create-customer-note"
                  placeholder="Catatan internal (opsional)"
                  value={creating.note}
                  onChange={(e) => setCreating({ ...creating, note: e.target.value })}
                />
              </div>
              <DialogFooter className="col-span-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setCreating(null)}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="btn-save-new-customer"
                  className="bg-zinc-900 hover:bg-zinc-800"
                >
                  {saving ? "Menyimpan…" : "Simpan Pelanggan"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-customer-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Pelanggan</DialogTitle>
            <DialogDescription>
              Perubahan nama & alamat akan berlaku untuk semua faktur pelanggan ini.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nama Pelanggan <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="edit-customer-name"
                  required
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Alamat</Label>
                <Input
                  data-testid="edit-customer-address"
                  placeholder="Alamat lengkap"
                  value={editing.address}
                  onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                />
              </div>
              <div>
                <Label>No HP / Telepon</Label>
                <Input
                  data-testid="edit-customer-phone"
                  placeholder="0812xxxxxxxx"
                  value={editing.phone}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Kota</Label>
                <Input
                  data-testid="edit-customer-city"
                  placeholder="Contoh: Jakarta Barat"
                  value={editing.city}
                  onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Email</Label>
                <Input
                  data-testid="edit-customer-email"
                  type="email"
                  placeholder="pelanggan@email.com"
                  value={editing.email}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>PIC</Label>
                <Input
                  data-testid="edit-customer-npwp"
                  placeholder="XX.XXX.XXX.X-XXX.XXX"
                  value={editing.npwp}
                  onChange={(e) => setEditing({ ...editing, npwp: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Catatan</Label>
                <Input
                  data-testid="edit-customer-note"
                  placeholder="Catatan internal (opsional)"
                  value={editing.note}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                />
              </div>
              <DialogFooter className="col-span-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="btn-save-customer"
                  className="bg-zinc-900 hover:bg-zinc-800"
                >
                  {saving ? "Menyimpan…" : "Simpan Perubahan"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
